/**
 * split-option-picker.js
 *
 * Three custom elements for handling Shopify "combined" options (e.g. "Shape + Color"):
 *
 *  1. SplitOptionPicker  - product page: renders two independent button groups from
 *                          a single combined variant option like "Curved - Black".
 *
 *  2. SplitColorFilter   - collection page: shows one color swatch per unique color
 *                          extracted from combined filter values (e.g. "Curved-Black",
 *                          "Round-Black"). Manages which Shopify combo filter params
 *                          get submitted based on the active shape + color selection.
 *
 *  3. SplitShapeFilter   - collection page: shows one checkbox per unique shape.
 *                          Visual only - all inputs have name="" so they never submit.
 *                          Updates split_shape hidden input and calls SplitColorFilter
 *                          to recalculate Shopify filter params.
 *
 * FILTER ARCHITECTURE:
 *
 *  split_shape (hidden input, form-level):
 *    Tracks user's shape selection. Submitted in URL as split_shape=Curved.
 *    Not a Shopify filter param - used only by our JS to drive SplitColorFilter.
 *
 *  split_color (hidden input, form-level):
 *    Tracks user's color selection. Submitted in URL as split_color=White.
 *    Not a Shopify filter param - used only by our JS to drive SplitColorFilter.
 *
 *  filter.v.option.shape+%2B+color (Shopify filter params):
 *    These ARE the real Shopify filter params that actually filter products.
 *    SplitColorFilter controls which combo values get submitted:
 *      - shape only:  submit all combos matching shape  (Curved-Black, Curved-White ...)
 *      - color only:  submit all combos matching color  (Curved-White, Round-White ...)
 *      - shape+color: submit only the specific combo    (Curved-White)
 *      - neither:     submit nothing                    (no filter)
 *
 *  CHECKED INPUT = THIS COMBO WILL BE SUBMITTED TO SHOPIFY
 *  The representative swatch/checkbox may appear visually checked even when no
 *  color is explicitly selected (shape-only mode). This is a known visual side-effect
 *  of using the same input for both visual state and Shopify param submission.
 */

// ---------------------------------------------------------------------------
// SplitOptionPicker (product page)
// ---------------------------------------------------------------------------

/**
 * @customElement split-option-picker
 *
 * Renders a combined "A + B" variant option (e.g. "Curved - Black") as two
 * independent UI groups: block buttons for part 0 (shape) and color swatches
 * for part 1 (color). Keeps the hidden set of Shopify radios in sync so the
 * VariantPicker fires its standard variant:change flow.
 *
 * @attr {string} form-id         - ID of the product form element
 * @attr {number} option-position - 1-indexed position of this option in the variant
 * @attr {string} separator       - string that separates the two parts (default: " - ")
 * @attr {string} white-labels    - comma-separated list of values that need ring-inset border
 * @attr {string} swatch-style    - "round" | "rectangle"
 */
class SplitOptionPicker extends HTMLElement {
  connectedCallback() {
    this._formId = this.getAttribute('form-id');
    this._optionPosition = parseInt(this.getAttribute('option-position'), 10);
    this._separator = this.getAttribute('separator') || '-';
    this._whiteLabels = (this.getAttribute('white-labels') || 'white').split(',').map(s => s.trim());
    this._swatchStyle = this.getAttribute('swatch-style') || 'round';

    // JSON data embedded by Liquid inside <script> tags within this element
    const valuesEl = this.querySelector('script[data-split-option-values]');
    const swatchEl = this.querySelector('script[data-split-swatch-map]');
    this._optionValues = JSON.parse(valuesEl.textContent);
    this._swatchMap = swatchEl ? JSON.parse(swatchEl.textContent) : {};

    this._buildState();

    // On init, fix any unavailable combination baked into the URL (e.g. a direct
    // link to a shape+trim combo that doesn't exist). Must run before _checkHiddenRadio
    // so the corrected other-option radios are in place before VariantPicker fires.
    this._checkHiddenRadio();
    this._render();

    // When another option changes (e.g. wattage), re-sync selected state from
    // the now-checked radio so our split UI stays in sync.
    this._abort = new AbortController();
    const form = document.getElementById(this._formId) || document.querySelector(`form[id="${this._formId}"]`);
    const target = form || document;
    target.addEventListener('variant:change', (e) => {
      this._syncFromRadios(false);
      if (!e.detail?.variant?.id) this._clickAvailableOptions();
    }, { signal: this._abort.signal });
  }

  disconnectedCallback() {
    // Clean up event listener to prevent memory leaks when element is removed
    this._abort?.abort();
  }

  /**
   * Split a combo name ("P0<sep>P1") into trimmed parts. Returns null if the
   * separator is absent. Trimmed because Shopify stores a blank part0 as
   * "- White" (extra space) rather than "-White".
   * @param {string} name
   * @returns {{p0: string, p1: string}|null}
   */
  _splitName(name) {
    const idx = name.indexOf(this._separator);
    if (idx === -1) return null;
    return {
      p0: name.slice(0, idx).trim(),
      p1: name.slice(idx + this._separator.length).trim(),
    };
  }

  /**
   * Find the option value whose trimmed parts match p0/p1. Compares parsed parts
   * rather than reconstructing "p0<sep>p1" as a literal string, since that
   * reconstruction can't reproduce Shopify's inconsistent blank-part0 spacing.
   * @param {string} p0
   * @param {string} p1
   * @returns {object|undefined}
   */
  _findCombo(p0, p1) {
    return this._optionValues.find(ov => {
      const parts = this._splitName(ov.name);
      return parts && parts.p0 === p0 && parts.p1 === p1;
    });
  }

  /**
   * Parse all combo values and extract unique part0 and part1 lists.
   * Also syncs initial selection from the checked radio.
   */
  _buildState() {
    const seen0 = new Set();
    const seen1 = new Set();
    this._part0Values = [];
    this._part1Values = [];

    for (const ov of this._optionValues) {
      const parts = this._splitName(ov.name);
      if (!parts) continue; // skip malformed values that lack separator
      const { p0, p1 } = parts;
      if (p0 !== '' && !seen0.has(p0)) { seen0.add(p0); this._part0Values.push(p0); }
      if (!seen1.has(p1)) { seen1.add(p1); this._part1Values.push(p1); }
    }

    this._syncFromRadios();
  }

  /**
   * Read which combo is currently selected from the hidden radio inputs in the form.
   * Falls back to JSON-marked selected value, then first available.
   * @param {boolean} allowJsonFallback - when false, skip the JSON fallback and leave
   *   current selection unchanged if no checked radio is found. Pass false from the
   *   variant:change listener to prevent stale Liquid-rendered state from overwriting
   *   a user selection when an unavailable variant clears the radio.
   */
  _syncFromRadios(allowJsonFallback = true) {
    const form = document.getElementById(this._formId) || document.querySelector(`form[id="${this._formId}"]`);
    const checkedInput = form
      ? Array.from(form.elements).find(el =>
          el.matches(`input[data-option-position="${this._optionPosition}"]:checked`))
      : null;

    if (checkedInput) {
      const checkedOv = this._optionValues.find(ov => String(ov.id) === String(checkedInput.value));
      if (checkedOv) {
        const parts = this._splitName(checkedOv.name);
        this._selectedPart0 = parts?.p0 ?? '';
        this._selectedPart1 = parts?.p1 ?? '';
        return;
      }
    }

    // At runtime (variant:change) skip the JSON fallback - the JSON reflects the original
    // page-load state and would revert the user's selection if the radio was cleared.
    if (!allowJsonFallback) return;

    // Fallback: Liquid marks the current variant's option value as selected in JSON
    const sel = this._optionValues.find(ov => ov.selected);
    if (sel) {
      const parts = this._splitName(sel.name);
      this._selectedPart0 = parts?.p0 ?? '';
      this._selectedPart1 = parts?.p1 ?? '';
    } else {
      this._selectedPart0 = this._part0Values[0];
      this._selectedPart1 = this._part1Values[0];
    }
  }

  /** Initial full render of both button groups and labels */
  _render() {
    const fieldset0 = this.querySelector('[data-split-buttons="0"]')?.closest('fieldset');
    if (this._part0Values.length === 0) {
      if (fieldset0) fieldset0.hidden = true;
    } else {
      if (fieldset0) fieldset0.hidden = false;
      this._renderPart(0, this._part0Values);
    }
    this._renderPart(1, this._part1Values);
    this._updateLabels();
  }

  /**
   * Render one button group (part 0 = block buttons, part 1 = color swatches).
   * @param {0|1} partIndex
   * @param {string[]} values
   */
  _renderPart(partIndex, values) {
    const container = this.querySelector(`[data-split-buttons="${partIndex}"]`);
    if (!container) return;
    container.innerHTML = '';

    for (const value of values) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.dataset.splitPart = partIndex;
      btn.dataset.splitValue = value;

      const isSelected = partIndex === 0 ? value === this._selectedPart0 : value === this._selectedPart1;
      const availability = this._availabilityFor(value, partIndex);

      if (partIndex === 1) {
        const isWhite = this._whiteLabels.includes(value.toLowerCase().trim());
        btn.className = [
          'color-swatch',
          this._swatchStyle === 'rectangle' ? 'color-swatch--rectangle' : 'rounded-full',
          isSelected ? 'is-selected' : '',
          availability === 'none' ? 'is-disabled' : '',
          isWhite ? 'ring-inset' : '',
        ].filter(Boolean).join(' ');

        const swatchStyle = this._swatchMap[value.toLowerCase().trim()];
        if (swatchStyle) btn.style.cssText = swatchStyle;

        const sr = document.createElement('span');
        sr.className = 'sr-only';
        sr.textContent = value;
        btn.appendChild(sr);
      } else {
        btn.className = [
          'block-swatch',
          isSelected ? 'is-selected' : '',
          availability === 'none' ? 'is-disabled' : '',
        ].filter(Boolean).join(' ');
        btn.textContent = value;
      }

      btn.addEventListener('click', () => this._onPartClick(value, partIndex));
      container.appendChild(btn);
    }
  }

  /**
   * Check availability of a value relative to the currently selected other part.
   * @param {string} value - the part value to check
   * @param {0|1} partIndex - which part this value belongs to
   * @returns {'available'|'sold_out'|'none'}
   */
  _availabilityFor(value, partIndex) {
    const relatedPart = partIndex === 0 ? this._selectedPart1 : this._selectedPart0;

    const comboOv = partIndex === 0
      ? this._findCombo(value, relatedPart)
      : this._findCombo(relatedPart, value);
    if (comboOv) return comboOv.available ? 'available' : 'sold_out';

    // Combo doesn't exist with current other selection - check if it exists at all
    const anyCombo = this._optionValues.find(ov => {
      const parts = this._splitName(ov.name);
      return parts && (partIndex === 0 ? parts.p0 : parts.p1) === value;
    });
    return anyCombo ? 'sold_out' : 'none';
  }

  /**
   * Handle user clicking a button in either group.
   * Updates selection state, finds a valid combo, syncs the hidden radio, re-renders.
   * @param {string} value - clicked value
   * @param {0|1} partIndex - which group was clicked
   */
  _onPartClick(value, partIndex) {
    if (partIndex === 0) {
      this._selectedPart0 = value;
    } else {
      this._selectedPart1 = value;
    }

    const targetOv = this._findCombo(this._selectedPart0, this._selectedPart1);

    if (!targetOv) {
      // The requested combo doesn't exist - find the best fallback keeping this part's value
      const fallback = this._optionValues.find(ov => {
        const parts = this._splitName(ov.name);
        return parts && (partIndex === 0 ? parts.p0 : parts.p1) === value && ov.available;
      }) || this._optionValues.find(ov => {
        const parts = this._splitName(ov.name);
        return parts && (partIndex === 0 ? parts.p0 : parts.p1) === value;
      });

      if (!fallback) return;

      const parts = this._splitName(fallback.name);
      this._selectedPart0 = parts.p0;
      this._selectedPart1 = parts.p1;
    }

    this._checkHiddenRadio();
    this._reRender();
    this._updateLabels();
  }

  /**
   * After a variant:change with no valid variant, find any checked option input whose
   * label has is-disabled and click the first non-disabled alternative for that position.
   */

  _clickAvailableOptions() {
    const form = document.getElementById(this._formId) || document.querySelector(`form[id="${this._formId}"]`);
    if (!form) return;
    const splitPos = String(this._optionPosition);

    for (const el of form.elements) {
      if (!el.matches('input[data-option-position]:checked')) continue;
      const pos = el.getAttribute('data-option-position');
      if (pos === splitPos) continue;
      if (!el.labels?.[0]?.classList.contains('is-disabled')) continue;

      const first = Array.from(form.elements).find(input =>
        input.matches(`input[data-option-position="${pos}"]:not(:checked)`) &&
        !input.labels?.[0]?.classList.contains('is-disabled')
      );
      if (first) { first.click(); return; }
    }
  }

  /**
   * Find and check the hidden radio for the current selection, then fire change
   * so VariantPicker updates price/availability/etc.
   */
  _checkHiddenRadio() {
    const targetOv = this._findCombo(this._selectedPart0, this._selectedPart1);
    if (!targetOv) return;

    const radio = this.querySelector(`input[data-option-position="${this._optionPosition}"][value="${targetOv.id}"]`);
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** Re-render both button groups without touching labels (called after selection change) */
  _reRender() {
    this._renderPart(0, this._part0Values);
    this._renderPart(1, this._part1Values);
  }

  /** Update the visible selected-value text labels */
  _updateLabels() {
    const label0 = this.querySelector('[data-split-selected-label="0"]');
    const label1 = this.querySelector('[data-split-selected-label="1"]');
    if (label0) label0.textContent = this._selectedPart0;
    if (label1) label1.textContent = this._selectedPart1;
  }

}

if (!window.customElements.get('split-option-picker')) {
  window.customElements.define('split-option-picker', SplitOptionPicker);
}

// ---------------------------------------------------------------------------
// Shared state for SplitColorFilter + SplitShapeFilter (collection page)
// ---------------------------------------------------------------------------

/**
 * Module-level selection state shared between ALL instances of SplitColorFilter
 * and SplitShapeFilter on the page (sidebar form + drawer form).
 *
 * WHY THIS EXISTS:
 *   The collection page renders TWO facet forms: one sidebar (update-on-change,
 *   auto-AJAX on each selection) and one drawer (apply-button, no auto-AJAX).
 *   When the user selects Black in the sidebar, FacetForm does an AJAX section
 *   update. The sidebar form is replaced with new HTML (split_color=Black).
 *   But the DRAWER form is in the same section and also gets replaced - however,
 *   its split_color hidden input now shows Black from the server render.
 *
 *   The real problem: if the user picks Black from the sidebar (sidebar AJAX fires)
 *   and then picks Curved from the DRAWER, the drawer form's SplitShapeFilter calls
 *   splitColorFilter._resync() on the DRAWER's SplitColorFilter. That _resync()
 *   reads from the drawer's split_color hidden input. If that input somehow has
 *   the stale value (empty) - e.g. due to any AJAX-timing issue or cross-form state
 *   leak - the color gets cleared.
 *
 *   Solution: store shape and color in a module-level object initialized from the
 *   page URL. All _onChange and _resync calls read/write this object. The hidden
 *   form inputs are kept in sync for FormData serialization, but are NOT the source
 *   of truth for filter state.
 *
 * LIFECYCLE:
 *   - Initialized once on script load from window.location.search
 *   - Updated by _onChange (user interaction) in both filter elements
 *   - Re-initialized if the page fully reloads (new script execution)
 *   - Sidebar AJAX does NOT reload the page, so the module var persists correctly
 */
const _splitState = (() => {
  const p = new URLSearchParams(window.location.search);
  return {
    shape: (p.get('split_shape') || '').trim(),
    color: (p.get('split_color') || '').trim(),
  };
})();

// ---------------------------------------------------------------------------
// SplitColorFilter (collection page)
// ---------------------------------------------------------------------------

/**
 * @customElement split-color-filter
 *
 * Groups combined filter value spans (e.g. Curved-Black, Round-Black) by color
 * and shows ONE visible swatch per unique color. Hidden duplicate spans carry the
 * real Shopify filter checkbox names. JS checks/unchecks them based on _splitState
 * to control which product-filter params get submitted to Shopify.
 *
 * CHECKED INPUT = THAT COMBO IS SUBMITTED TO SHOPIFY AS AN ACTIVE FILTER.
 *
 * Decision matrix (_splitState.shape / _splitState.color):
 *   shape=''  color=''  -> no inputs checked (no filter)
 *   shape=X   color=''  -> all combos where shape===X are checked
 *   shape=''  color=Y   -> all combos where color===Y are checked
 *   shape=X   color=Y   -> only the X-Y combo is checked
 */
class SplitColorFilter extends HTMLElement {
  connectedCallback() {
    /**
     * Map of color label -> array of all spans with that color.
     * Each span wraps one Shopify filter checkbox input.
     * @example { 'Black': [span(Curved-Black), span(Round-Black), span(Square-Black)] }
     * @type {Map<string, HTMLElement[]>}
     */
    this._groups = new Map();

    this.querySelectorAll('[data-split-color]').forEach(span => {
      const color = span.dataset.splitColor;
      if (!this._groups.has(color)) this._groups.set(color, []);
      this._groups.get(color).push(span);
    });

    // Sync hidden inputs to match _splitState.
    // The server may have rendered these with stale values if the section was AJAX-
    // replaced. _splitState always has the correct committed selection from the URL.
    const { colorInput, shapeInput } = this._formInputs();
    if (colorInput) colorInput.value = _splitState.color;
    if (shapeInput) shapeInput.value = _splitState.shape;

    this._resync();
    _renderSplitPills();

    this.addEventListener('change', e => this._onChange(e));
  }

  /**
   * Get the form and its split_color / split_shape hidden inputs.
   * These inputs are kept in sync with _splitState so FormData serializes correctly.
   * @returns {{ form, colorInput, shapeInput }}
   */
  _formInputs() {
    const form = this.closest('form');
    return {
      form,
      colorInput: form?.querySelector('input[name="split_color"]'),
      shapeInput: form?.querySelector('input[name="split_shape"]'),
    };
  }

  /**
   * Recalculate which Shopify filter inputs are checked and which swatches are visible.
   * Reads from _splitState (module-level, shared, always up to date).
   * Called on: connectedCallback, shape selection (_onChange in SplitShapeFilter),
   *            color selection (_onChange here).
   *
   * VISIBILITY: One representative span per color is shown; others are hidden.
   *   When color is active + shape set, representative = the span for that combo,
   *   so the visible swatch confirms the exact selection.
   *   Otherwise, representative = spans[0].
   *
   * CHECKED: input.checked = hasFilter AND shapeMatch AND colorMatch
   *   hasFilter   = shape != '' OR color != ''
   *   shapeMatch  = shape == '' OR span.data-split-combo-shape == shape
   *   colorMatch  = color == '' OR group color key == color
   */
  _resync() {
    const activeShape = _splitState.shape.toLowerCase();
    const activeColor = _splitState.color.toLowerCase();
    const hasFilter   = activeShape !== '' || activeColor !== '';

    for (const [color, spans] of this._groups.entries()) {
      const colorKey    = color.trim().toLowerCase();
      const colorActive = activeColor !== '' && colorKey === activeColor;

      // Pick the representative span to show as the visible swatch.
      // When this color IS selected and a shape is set, show the shape-matching span
      // so the swatch visually reflects the exact combo. Otherwise use first span.
      const representative = colorActive && activeShape !== ''
        ? (spans.find(s => (s.dataset.splitComboShape ?? '').trim().toLowerCase() === activeShape) ?? spans[0])
        : spans[0];

      for (const span of spans) {
        const input = span.querySelector('input');
        if (!input) continue;

        span.hidden = span !== representative;

        const spanShape  = (span.dataset.splitComboShape ?? '').trim().toLowerCase();
        const shapeMatch = activeShape === '' || spanShape === activeShape;
        const colorMatch = activeColor === '' || colorKey === activeColor;

        input.checked = hasFilter && shapeMatch && colorMatch;
      }
    }
  }

  /**
   * Handle the user clicking a color swatch checkbox.
   * Writes to _splitState, keeps hidden input in sync, resyncs, dispatches.
   *
   * The 'change' event is dispatched from the FORM-LEVEL split_color input
   * (outside this element) so FacetForm hears it exactly once without this
   * element's own listener re-firing.
   *
   * @param {Event} event
   */
  _onChange(event) {
    // Only react to real user interactions.
    // Programmatic input.checked changes (from _resync) must not update _splitState.
    if (!event.isTrusted) return;

    const span = event.target.closest('[data-split-color]');
    if (!span) return;

    event.stopPropagation();

    const color     = span.dataset.splitColor;
    const isChecked = event.target.checked;

    // Write to shared state first - this is the source of truth
    _splitState.color = isChecked ? color.trim() : '';

    // Keep the hidden form input in sync for FormData serialization
    const { colorInput } = this._formInputs();
    if (colorInput) colorInput.value = _splitState.color;

    this._resync();
    colorInput?.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!window.customElements.get('split-color-filter')) {
  window.customElements.define('split-color-filter', SplitColorFilter);
}

// ---------------------------------------------------------------------------
// SplitShapeFilter (collection page)
// ---------------------------------------------------------------------------

/**
 * @customElement split-shape-filter
 *
 * Groups combined filter value spans by their shape part (P0 of "Shape-Color").
 * Shows ONE checkbox per unique shape. All inputs are visual-only (name='') and
 * never submit to Shopify. On selection, writes to _splitState, keeps the hidden
 * split_shape input in sync, calls SplitColorFilter._resync(), and dispatches
 * 'change' from the hidden input to trigger FacetForm.
 *
 * WHY name='':
 *   FormData skips inputs without a name. Setting name='' keeps inputs interactive
 *   (clickable) but excluded from serialization. Using disabled would break clicks.
 */
class SplitShapeFilter extends HTMLElement {
  connectedCallback() {
    /**
     * Map of shape label -> array of all spans with that shape.
     * Multiple spans per shape because Shopify generates one value per combo
     * (e.g. Curved has spans for Curved-Black, Curved-White, etc.).
     * @type {Map<string, HTMLElement[]>}
     */
    this._groups = new Map();

    this.querySelectorAll('[data-split-shape]').forEach(span => {
      const shape = span.dataset.splitShape;
      if (!this._groups.has(shape)) this._groups.set(shape, []);
      this._groups.get(shape).push(span);
    });

    // Sync the hidden split_shape input from _splitState (same AJAX-safety reason
    // as SplitColorFilter - server-rendered value may be stale after section replace)
    const form        = this.closest('form');
    const shapeInput  = form?.querySelector('input[name="split_shape"]');
    if (shapeInput) shapeInput.value = _splitState.shape;

    // Set visual checked state of representative shape checkboxes from _splitState
    for (const [shape, spans] of this._groups.entries()) {
      const isSelected = _splitState.shape !== '' && shape.trim() === _splitState.shape;
      spans.forEach((span, i) => {
        const input = span.querySelector('input');
        if (!input) return;
        if (i > 0) span.hidden = true; // only first span per shape is visible
        input.name    = '';            // visual-only: excluded from FormData
        input.checked = isSelected;
      });
    }

    this.addEventListener('change', e => this._onChange(e));
  }

  /**
   * Handle user checking/unchecking a shape checkbox.
   * Writes to _splitState, syncs hidden input, resyncs color filter, dispatches.
   *
   * @param {Event} event
   */
  _onChange(event) {
    // Only react to real user interactions.
    // Programmatic checked changes (from the radio-like deselect loop below)
    // must not update _splitState.
    if (!event.isTrusted) return;

    const span = event.target.closest('[data-split-shape]');
    if (!span) return;

    event.stopPropagation();

    const shape     = span.dataset.splitShape;
    const isChecked = event.target.checked;
    const form      = this.closest('form');
    const shapeInput = form?.querySelector('input[name="split_shape"]');

    // Write to shared state - this is the source of truth
    _splitState.shape = isChecked ? shape.trim() : '';

    // Keep hidden form input in sync for FormData
    if (shapeInput) shapeInput.value = _splitState.shape;

    // Update visual state of shape checkboxes (radio-like: only one active)
    for (const [s, spans] of this._groups.entries()) {
      const shouldCheck = isChecked && s === shape;
      const repInput    = spans[0]?.querySelector('input');
      if (repInput) repInput.checked = shouldCheck;
    }

    // SplitColorFilter owns all real Shopify filter param submission.
    // Tell it to recalculate which combo inputs to check given the new shape.
    const splitColorFilter = form?.querySelector('split-color-filter');
    if (splitColorFilter?._resync) splitColorFilter._resync();

    // Dispatch from the form-level hidden input so FacetForm hears it exactly once
    shapeInput?.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!window.customElements.get('split-shape-filter')) {
  window.customElements.define('split-shape-filter', SplitShapeFilter);
}

// ---------------------------------------------------------------------------
// Split option active pills
// ---------------------------------------------------------------------------

/**
 * Render removable pills for the active split_shape / split_color URL params
 * into every .active-facets container on the page (desktop + mobile duplicates).
 *
 * Called from SplitColorFilter.connectedCallback (fires on each AJAX section
 * replace) and once at module load as a fallback for pages that have no
 * split-color-filter element.
 */
function _renderSplitPills() {
  const shape = _splitState.shape;
  const color = _splitState.color;
  const hasActive = shape || color;

  // Remove any previously inserted split pills so re-renders are clean
  document.querySelectorAll('[data-split-pill]').forEach(el => el.remove());

  // Build target list: get/create .active-facets inside each .facets-summary,
  // then append any other pre-existing .active-facets (e.g. mobile duplicate)
  const targets = [];
  document.querySelectorAll('.facets-summary').forEach(summary => {
    let af = summary.querySelector('.active-facets');
    if (!af && hasActive) {
      af = document.createElement('div');
      af.className = 'active-facets';
      summary.prepend(af);
    }
    if (af) targets.push(af);
  });
  document.querySelectorAll('.active-facets').forEach(af => {
    if (!targets.includes(af)) targets.push(af);
  });

  if (!hasActive || targets.length === 0) return;

  // Collect combo inputs from the first split-color-filter only (sidebar and
  // drawer are identical; using both would double-submit every param)
  const firstColorFilter = document.querySelector('split-color-filter');
  const comboInputs = firstColorFilter
    ? Array.from(firstColorFilter.querySelectorAll('[data-split-color] input[name]'))
    : [];
  const basePath = window.location.pathname;
  const sortBy = new URLSearchParams(window.location.search).get('sort_by');

  function buildRemoveUrl(keepShape, keepColor) {
    if (!keepShape && !keepColor) return basePath;
    const params = new URLSearchParams();
    if (keepShape) params.set('split_shape', keepShape);
    if (keepColor) params.set('split_color', keepColor);
    if (sortBy) params.set('sort_by', sortBy);
    comboInputs.forEach(input => {
      const span = input.closest('[data-split-color]');
      const comboShape = (span?.dataset.splitComboShape || '').trim();
      const comboColor = (span?.dataset.splitColor || '').trim();
      if (!comboShape) return; // skip malformed combos with no shape part
      const shapeOk = !keepShape || comboShape === keepShape;
      const colorOk = !keepColor || comboColor.toLowerCase() === keepColor.toLowerCase();
      if (shapeOk && colorOk) params.append(input.name, input.value);
    });
    return basePath + '?' + params.toString();
  }

  function makePill(label, removeUrl, type) {
    const div = document.createElement('div');
    div.className = 'removable-facet';
    div.dataset.splitPill = type;
    div.append(document.createTextNode(label));
    const a = document.createElement('a', { is: 'facet-link' });
    a.href = removeUrl;
    a.className = 'tap-area';
    a.setAttribute('aria-label', `Remove filter "${label}"`);
    a.setAttribute('data-no-instant', '');
    a.innerHTML = '<svg role="presentation" stroke-width="2" focusable="false" width="10" height="10" class="icon icon-delete" viewBox="0 0 10 10"><path d="m.757.757 8.486 8.486m-8.486 0L9.243.757" stroke="currentColor"></path></svg>';
    // Clear _splitState before FacetLink triggers AJAX so connectedCallback
    // reads the correct (empty) state and does not re-render the removed pill
    a.addEventListener('click', () => {
      if (type === 'shape') _splitState.shape = '';
      else _splitState.color = '';
    }, { capture: true });
    div.appendChild(a);
    return div;
  }

  targets.forEach(af => {
    if (shape) af.prepend(makePill(shape, buildRemoveUrl('', color), 'shape'));
    if (color) {
      const pill = makePill(color, buildRemoveUrl(shape, ''), 'color');
      if (shape && af.children[0]) {
        af.children[0].after(pill);
      } else {
        af.prepend(pill);
      }
    }
  });
}

// Fallback: render pills on initial load for pages without split-color-filter
_renderSplitPills();
